import React, { useState } from 'react';
import Checkbox from '../../../../components/common/checkbox/checkbox';
import { useUserAccess } from '../../../../contexts/userAccess';
import { User } from '../../admin';
import './newUserForm.css';
import { useApiData } from '../../../../contexts/apiService';

interface NewUser {
    email: string;
    password: string;
    name: string;
    company: string;
    producer_id: number | null;
    section: string;
    role: string;
    Cc: string;
    sendWelcomeEmail: boolean;
}

interface NewUserFormProps {
    uniqueProducers: { id: number | null; name: string }[];
    users: User[];
    onUserCreated: () => void;
}

interface FormField {
    name: string;
    label: string;
    required: boolean;
    autocomplete?: string;
}

const formFields: FormField[] = [
    { name: 'email', label: 'Email', required: true, autocomplete: 'off' },
    { name: 'password', label: 'Password', required: true, autocomplete: 'off' },
    { name: 'name', label: 'Name', required: true, autocomplete: 'off' },
    { name: 'company', label: 'Company', required: true },
    { name: 'section', label: 'Section', required: false, autocomplete: 'off' },
    { name: 'role', label: 'Role', required: false },
] as const;

const NewUserForm: React.FC<NewUserFormProps> = ({ 
    users,
    uniqueProducers,
    onUserCreated,
}) => {
    
    const { userEmail } = useUserAccess();
    const { handleCreateUser } = useApiData();
    const [error, setError] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const [newUser, setNewUser] = useState<NewUser>({
        email: '',
        password: '',
        name: '',
        company: '',
        producer_id: null,
        section: '',
        role: '',
        Cc: '',
        sendWelcomeEmail: true,
    });

    const emailOptions = [
        { label: 'Myself', email: userEmail },
        { label: 'Systems', email: 'systems@rockbridgepetroleum.com' },
        { label: 'Brett', email: 'brett@rockbridgepetroleum.com' },
        { label: 'Brian', email: 'brian@rockbridgepetroleum.com' },
    ];

    const isFormValid = () => {
        return newUser.email.trim() !== '' && 
               newUser.password.trim() !== '' && 
               newUser.name.trim() !== '' && 
               newUser.company.trim() !== '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;

        // setting 'company' string value now also sets producer id - can remove later when transition to producer_id is complete
        if (name === 'company') {
            const selectedProducer = uniqueProducers.find(p => p.name === value);
            setNewUser(prev => ({
                ...prev,
                company: value,
                producer_id: selectedProducer?.id ?? null,
            }));
            return;
        }
        
        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSendWelcomeEmailChange = (checked: boolean) => {
        setNewUser(prev => ({
            ...prev,
            sendWelcomeEmail: checked
        }));
    };

    const isDisabled = isCreating || !isFormValid();

    const handleToggleEmail = (email: string) => {
        setNewUser(prev => {
            const currentEmails = prev.Cc.split(',').map(e => e.trim()).filter(e => e);
            
            if (currentEmails.includes(email)) {
                // Remove email if it exists
                const updatedEmails = currentEmails.filter(e => e !== email);
                return {
                    ...prev,
                    Cc: updatedEmails.join(', ')
                };
            } else {
                // Add email if it doesn't exist
                const newCc = currentEmails.length > 0 
                    ? `${prev.Cc}, ${email}` 
                    : email;
                return {
                    ...prev,
                    Cc: newCc
                };
            }
        });
    };

    // Check if email is currently in the CC list
    const isEmailInCc = (email: string) => {
        const currentEmails = newUser.Cc.split(',').map(e => e.trim()).filter(e => e);
        return currentEmails.includes(email);
    };

    const submitCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsCreating(true);
        setError('');
        
        // Check if email already exists
        const emailExists = users.some(user => user.email.toLowerCase() === newUser.email.toLowerCase());
        
        if (emailExists) {
            setError('A user with this email already exists');
            setIsCreating(false);
            return;
        }

        if (newUser.email) {
            const email = newUser.email.trim();
            const atIndex = email.indexOf('@');
            if (atIndex === -1 || !email.substring(atIndex + 1).includes('.')) {
                setError('Invalid user email');
                setIsCreating(false);
                return;
            }
        }

        if (newUser.password && newUser.password.length < 6) {
            setError('Password must be at least 6 characters long');
            setIsCreating(false);
            return;
        }

        try {
            // Call the handleCreateUser from context, passing in the data from newUser
            await handleCreateUser(
                newUser.email,
                newUser.name,
                newUser.password,
                newUser.company,
                newUser.producer_id,
                newUser.section,
                newUser.role,
                newUser.sendWelcomeEmail,
                newUser.Cc,
            );
        
            // Reset the form except company and Cc (since most times its the same)
            setNewUser({
                email: '',
                name: '',
                password: '',
                company: newUser.company,
                producer_id: newUser.producer_id,
                section: '',
                role: '',
                Cc: newUser.Cc,
                sendWelcomeEmail: newUser.sendWelcomeEmail,
            });
        
            // Then refresh the users list
            if (onUserCreated) {
                await onUserCreated(); // You can await it if needed
            }
            
        } catch (err) {
            console.error('Error creating user:', err);
            setError('Failed to create user. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };
    
    return (
        <div className="userForm-mainContainer">
            
            <h2 className="userForm-titleContainer">
                Create New User
            </h2>

            <div className="global-rowContainer">
                <div className="userForm-leftContainer">
                    
                    <div className="userForm-formContainer">
                        <form>
                            {formFields.map(field => (
                                <div key={field.name} className="userForm-fieldContainer">
                                    <label htmlFor={field.name} className="userForm-formLabel">
                                        {field.label}{field.required && ' *'}
                                    </label>
                                    {field.name === 'company' ? (
                                        <select
                                            className="userForm-formInput"
                                            id={field.name}
                                            name={field.name}
                                            value={newUser.company}
                                            onChange={handleSelectChange}
                                            required={field.required}
                                        >
                                            <option value="" disabled></option>
                                            {[...uniqueProducers]
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((producer) => (
                                                    <option
                                                        key={`${producer.id ?? 'null'}-${producer.name}`}
                                                        value={producer.name}
                                                    >
                                                        {producer.name}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    ) : (
                                        <input
                                            className="userForm-formInput"
                                            id={field.name}
                                            name={field.name}
                                            value={String(newUser[field.name as keyof typeof newUser])}
                                            onChange={handleChange}
                                            required={field.required}
                                            autoComplete={field.autocomplete || 'on'}
                                        />
                                    )}
                                </div>
                            ))}
                        </form>
                    </div>
                </div>

                <div className="userForm-rightContainer">
                    <div
                        className="global-rowContainer"
                        style={{ alignItems: 'center', gap: '10px' }}
                    >
                        <button
                            onClick={submitCreateUser}
                            type="submit"
                            disabled={isDisabled}
                            className={`userForm-submitButton ${isDisabled ? 'userForm-submitButton-disabled' : 'userForm-submitButton-enabled'}`}
                        >
                            {isCreating ? 'Creating...' : 'Create User'}
                        </button>

                        <div>
                            <Checkbox
                                label="Send welcome email"
                                defaultChecked={newUser.sendWelcomeEmail}
                                onChange={handleSendWelcomeEmailChange}
                            />
                        </div>
                    </div>
                    
                    {/* Conditional rendering - much simpler! */}
                    {newUser.sendWelcomeEmail && (
                        <div className="userForm-ccContainer">
                            <input
                                className="userForm-ccInput"
                                name='Cc'
                                value={newUser.Cc}
                                placeholder="Email copy..."
                                onChange={handleChange}
                                autoComplete="off"
                            />
                            <div 
                                className="userForm-rowContainer"
                                style={{ gap: '10px'}}
                            >
                                {emailOptions.map(option => (
                                    <button
                                        key={option.label}
                                        type="button"
                                        className={`userForm-ccAddButton ${isEmailInCc(option.email) ? 'active' : ''}`}
                                        onClick={() => handleToggleEmail(option.email)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="userForm-errorText">{error}</div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default NewUserForm;